import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  useTheme,
  alpha,
  Tabs,
  Tab,
  IconButton,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import LoginIcon from "@mui/icons-material/Login";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

function LobbyArea({ socket, showAlert, onBack }) {
  const [tab, setTab] = useState(0);
  const [username, setUsername] = useState("");
  const [joinCode, setJoinCode] = useState("");
  const [totalRounds, setTotalRounds] = useState(5);
  const theme = useTheme();

  const handleCreate = () => {
    const name = username.trim();
    if (!name) return showAlert("Username Required", "Please enter a username.", "error");
    if (name.length < 2 || name.length > 15) return showAlert("Invalid Name", "Username must be 2–15 characters.", "error");
    const playerId = socket.playerId || localStorage.getItem("playerId");
    socket.emit("createRoom", { username: name, totalRounds, playerId });
  };

  const handleJoin = () => {
    const name = username.trim();
    const code = joinCode.trim().toUpperCase();
    if (!name) return showAlert("Username Required", "Please enter a username.", "error");
    if (name.length < 2 || name.length > 15) return showAlert("Invalid Name", "Username must be 2–15 characters.", "error");
    if (!code) return showAlert("Room Code Required", "Please enter a room code.", "error");
    const playerId = socket.playerId || localStorage.getItem("playerId");
    socket.emit("joinRoom", { roomCode: code, username: name, playerId });
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
  };

  const contentVariants = {
    initial: { opacity: 0, x: tab === 0 ? -20 : 20 },
    animate: { opacity: 1, x: 0, transition: { duration: 0.3 } },
    exit: { opacity: 0, x: tab === 0 ? 20 : -20, transition: { duration: 0.2 } },
  };

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        py: 4,
      }}
    >
      <motion.div variants={cardVariants} initial="hidden" animate="visible">
        <Box
          className="glass-panel"
          sx={{
            width: { xs: "100%", sm: 420 },
            maxWidth: 420,
            background: theme.palette.glass.bg,
            border: `1px solid ${theme.palette.glass.border}`,
            borderRadius: 3,
            boxShadow: theme.palette.glass.shadow,
            overflow: "hidden",
          }}
        >
          {/* Brand header */}
          <Box sx={{ textAlign: "center", pt: 4, pb: 2, px: 3, position: "relative" }}>
            {onBack && (
              <IconButton
                onClick={onBack}
                sx={{ position: "absolute", top: 16, left: 16, color: theme.palette.text.secondary }}
                size="small"
              >
                <ArrowBackIcon />
              </IconButton>
            )}
            <Box
              component="img"
              src="/assets/logo/rmcs-logo.png"
              alt="RMCS"
              sx={{
                width: 56,
                height: 56,
                mb: 1.5,
                borderRadius: 2,
                animation: "float 3s ease-in-out infinite",
              }}
            />
            <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: 1.5 }}>
              RMCS
            </Typography>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 0.5 }}>
              Raja, Mantri, Chor & Sipahi
            </Typography>
          </Box>

          {/* Username (shared) */}
          <Box sx={{ px: 3, mb: 1 }}>
            <TextField
              fullWidth
              label="Your Name"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              inputProps={{ maxLength: 15 }}
              size="small"
            />
          </Box>

          {/* Tabs */}
          <Box sx={{ px: 3 }}>
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              variant="fullWidth"
              TabIndicatorProps={{
                sx: {
                  height: 3,
                  borderRadius: 2,
                  backgroundColor: theme.palette.primary.main,
                },
              }}
              sx={{
                minHeight: 40,
                backgroundColor: alpha(theme.palette.background.default, 0.5),
                borderRadius: 2,
                mb: 2,
              }}
            >
              <Tab label="Create" icon={<AddCircleOutlineIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
              <Tab label="Join" icon={<LoginIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
            </Tabs>
          </Box>

          {/* Tab content */}
          <Box sx={{ px: 3, pb: 3 }}>
            <AnimatePresence mode="wait">
              {tab === 0 ? (
                <motion.div key="create" variants={contentVariants} initial="initial" animate="animate" exit="exit">
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Number of Rounds"
                      value={totalRounds}
                      onChange={(e) => {
                        // Allow free typing; clamp on blur
                        const raw = e.target.value;
                        if (raw === "") { setTotalRounds(""); return; }
                        const v = parseInt(raw, 10);
                        if (!isNaN(v)) setTotalRounds(v);
                      }}
                      onBlur={() => {
                        const v = parseInt(totalRounds, 10);
                        if (isNaN(v) || v < 1) setTotalRounds(1);
                        else if (v > 20) setTotalRounds(20);
                        else setTotalRounds(v);
                      }}
                      inputProps={{ min: 1, max: 20, inputMode: "numeric", style: { textAlign: "center", fontWeight: 700 } }}
                      size="small"
                      helperText="1 – 20 rounds"
                      sx={{
                        "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": { display: "none" },
                        "& input[type=number]": { MozAppearance: "textfield" },
                      }}
                    />
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={handleCreate}
                      size="large"
                      className="glow-hover"
                      sx={{ py: 1.25 }}
                    >
                      Create Room
                    </Button>
                  </Box>
                </motion.div>
              ) : (
                <motion.div key="join" variants={contentVariants} initial="initial" animate="animate" exit="exit">
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <TextField
                      fullWidth
                      label="Room Code"
                      value={joinCode}
                      onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                      inputProps={{ maxLength: 6 }}
                      size="small"
                      sx={{
                        "& input": { letterSpacing: 3, fontWeight: 700, textAlign: "center" },
                      }}
                    />
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={handleJoin}
                      size="large"
                      className="glow-hover"
                      sx={{ py: 1.25 }}
                    >
                      Join Room
                    </Button>
                  </Box>
                </motion.div>
              )}
            </AnimatePresence>
          </Box>
        </Box>
      </motion.div>
    </Box>
  );
}

export default LobbyArea;
